import { useMemo } from 'react';
import {
    META_MAX,
    META_MIN,
    SEO_TITLE_MAX,
    overallStatus,
    searchSnippet,
    storySeoChecks,
} from 'utils/seo-checks';
import styles from 'styles/modules/newsAlert/Newsroom.module.scss';

const STATUS_LABEL = {
    good: 'Good',
    ok: 'OK',
    bad: 'Needs work',
    info: 'Note',
};

const LengthBar = ({ length, min, max }) => {
    const status =
        !length || length > max ? 'bad' : length < min ? 'ok' : 'good';
    const width = Math.min(100, Math.round((length / max) * 100));
    return (
        <div className={styles.lengthBar} aria-hidden="true">
            <span
                className={styles[`bar-${status}`]}
                style={{ width: `${width}%` }}
            />
        </div>
    );
};

const CheckList = ({ title, checks }) => (
    <div className={styles.checkGroup}>
        <h4>{title}</h4>
        <ul className={styles.checks}>
            {checks.map((check) => (
                <li key={check.id}>
                    <span
                        className={[
                            styles.dot,
                            styles[`dot-${check.status}`],
                        ].join(' ')}
                        title={STATUS_LABEL[check.status]}
                    />
                    <span>
                        <strong>{check.label}:</strong> {check.text}
                    </span>
                </li>
            ))}
        </ul>
    </div>
);

// The Yoast-style side panel: focus keyphrase, Google preview, SEO title and
// meta description with length bars, then the SEO and readability checks.
const SeoPanel = ({ draft, slug, onChange }) => {
    const checks = useMemo(
        () => storySeoChecks(draft, { slug }),
        [draft, slug]
    );
    const snippet = searchSnippet(draft, slug);
    const overall = overallStatus(checks);
    const seoTitleLength = (draft.seoTitle || draft.headline || '').trim()
        .length;
    const metaLength = (draft.metaDescription || '').trim().length;

    return (
        <div className={styles.seo}>
            <div className={styles.seoHead}>
                <span
                    className={[styles.dot, styles[`dot-${overall}`]].join(' ')}
                />
                <strong>SEO: {STATUS_LABEL[overall]}</strong>
            </div>

            <label className={styles.label} htmlFor="story-keyphrase">
                Focus keyphrase
            </label>
            <input
                id="story-keyphrase"
                className={styles.input}
                value={draft.focusKeyphrase}
                maxLength={255}
                placeholder="The search you want to rank for"
                onChange={(e) => onChange('focusKeyphrase', e.target.value)}
            />

            <div className={styles.snippet} aria-label="Google preview">
                <span className={styles.snippetUrl}>{snippet.url}</span>
                <span className={styles.snippetTitle}>{snippet.title}</span>
                <span className={styles.snippetText}>
                    {snippet.description}
                </span>
            </div>

            <label className={styles.label} htmlFor="story-seo-title">
                SEO title{' '}
                <span className={styles.count}>
                    {seoTitleLength}/{SEO_TITLE_MAX}
                </span>
            </label>
            <input
                id="story-seo-title"
                className={styles.input}
                value={draft.seoTitle}
                maxLength={255}
                placeholder={draft.headline || 'Defaults to the headline'}
                onChange={(e) => onChange('seoTitle', e.target.value)}
            />
            <LengthBar length={seoTitleLength} min={30} max={SEO_TITLE_MAX} />

            <label className={styles.label} htmlFor="story-meta">
                Meta description{' '}
                <span className={styles.count}>
                    {metaLength}/{META_MAX}
                </span>
            </label>
            <textarea
                id="story-meta"
                className={styles.textarea}
                rows={3}
                value={draft.metaDescription}
                maxLength={500}
                placeholder="What Google shows under the title"
                onChange={(e) => onChange('metaDescription', e.target.value)}
            />
            <LengthBar length={metaLength} min={META_MIN} max={META_MAX} />

            <CheckList
                title="SEO analysis"
                checks={checks.filter((c) => c.group === 'seo')}
            />
            <CheckList
                title="Readability"
                checks={checks.filter((c) => c.group === 'readability')}
            />
        </div>
    );
};

export default SeoPanel;
